import React, { useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './assets/css/search.css';
import './assets/css/design_system_v2.css';
import './assets/css/sb.css';
import './assets/css/override.css';
import './assets/css/library.css';
import './assets/css/aws_weather.css';
import './assets/css/user_booking.css';

import {
  BrowserRouter as Router,
  matchPath,
  useLocation,
} from 'react-router-dom';
import BaseRouter from './routes';
import axios from 'axios';
import {
  CookieConsent,
  Cookies,
  getCookieConsentValue,
} from 'react-cookie-consent';
import flagsmith from 'flagsmith';
import { FlagsmithProvider } from 'flagsmith/react';
import {
  useLocalStorage,
  usePreventScroll,
  useQuery,
  UserIdentificationProvider,
} from './utils/hooks';

import shieldImage from './assets/images/shieldGreen.png';
import { CloseOutlined } from '@ant-design/icons';
import { Button } from 'antd';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import timezone from 'dayjs/plugin/timezone';
import { PRIVACY_POLICY_URL } from './AppConstants';
import {
  AnalyticsEvents,
  hasCookieConsent,
  pushDataLayerEvent,
} from './utils/analytics';
import { CookiePreferencesContext } from './utils/cookiePreferences';

dayjs.extend(customParseFormat);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(advancedFormat);
dayjs.extend(utc);

const instance = axios.create({
  withCredentials: true,
  crossDomain: true,
  baseURL: `${process.env.REACT_APP_AXIOS_URL}fishery/auth`,
  'Access-Control-Allow-Origin': '*',
  sameSite: null,
  secure: false,
});

const refreshToken = () => {
  instance
    .get('refresh')
    .then((res) => {})
    .catch((err) => console.log(err))
    .finally(() => {});
};

// Half a minute
// const MINUTE_MS = 30000;

// 29.5 Minutes
// const MINUTE_MS = 1770000;

// 10 Minutes
const MINUTE_MS = 600000;

setInterval(() => {
  refreshToken();
}, MINUTE_MS);

function CookieConsentBanner({
  forceOpen = false,
  onAccept = () => {},
  onClose = () => {},
}) {
  const cookieExpiry = 182; /* ~6 months */
  const hasGrantedThisVisitRef = useRef(false);

  /**
   * From https://github.com/Mastermindzh/react-cookie-consent/blob/master/src/CookieConsent.tsx
   */
  const setConsentCookie = (cookieValue) => {
    const cookieSecurity = window.location
      ? window.location.protocol === 'https:'
      : true;
    const cookieOptions = {
      expires: cookieExpiry,
      sameSite: 'lax',
      secure: cookieSecurity,
    };
    Cookies.set('CookieConsent', cookieValue, cookieOptions);
  };

  const grantCookiesConsent = () => {
    window.grantGTagConsent();
    window.grantFBPixelConsent();
    window.loadHeap();
    hasGrantedThisVisitRef.current = true;
  };

  const onCookieSet = () => {
    setIsOpen(false);
    onClose();
  };

  const handleAccept = () => {
    if (!hasGrantedThisVisitRef.current) {
      pushDataLayerEvent(AnalyticsEvents.ConsentGranted);
    }
    grantCookiesConsent();
    onAccept();
    onCookieSet();
  };

  // Trackers that already started can't be switched off (Heap has no
  // unload), so withdrawing consent mid-visit reloads the page, which starts
  // them in their denied state.
  const handleDecline = () => {
    if (hasGrantedThisVisitRef.current) {
      window.location.reload();
      return;
    }
    onCookieSet();
  };

  // Dismissing the banner declines on first visit, but when it was reopened
  // from "Cookie settings" it keeps the choice already made.
  const handleDismiss = () => {
    if (forceOpen) {
      onCookieSet();
      return;
    }
    setConsentCookie(false);
    handleDecline();
  };

  const { pathname } = useLocation();
  const isPathWidget = Boolean(
    matchPath(pathname, {
      path: '/external',
      exact: false,
    })
  );
  const isQueryWidget = JSON.parse(useQuery().get('widget') || 'null');
  const isWidget = isPathWidget || isQueryWidget;

  // Only check once on page load (not on resize) for performance reasons
  const isDesktop = Boolean(window?.innerWidth > 576);
  const [inWebview] = useLocalStorage('adminIsInWebview', false);
  const showOverlay = !isDesktop;
  const [isOpen, setIsOpen] = useState(!Boolean(getCookieConsentValue()));
  const cookieDomain = process.env?.REACT_APP_COOKIE_DOMAIN;
  const isBannerOpen = (isOpen || forceOpen) && !isWidget && !inWebview;
  const bannerVisibility = forceOpen ? 'show' : 'byCookieValue';

  // Prevent background app scroll when drawer is open
  usePreventScroll('CookieConsentBanner', isBannerOpen && showOverlay);

  /**
   * Grant cookie permissions if consent is already given on page load
   */
  useEffect(() => {
    if (hasCookieConsent()) {
      grantCookiesConsent();
    }
  }, []);

  const isFisheryProfilePage = matchPath(pathname, {
    path: '/fishery',
    exact: false,
  });
  const isSearchPage = matchPath(pathname, {
    path: '/search',
    exact: false,
  });

  return (
    <CookieConsent
      extraCookieOptions={{
        ...(cookieDomain ? { domain: cookieDomain } : {}),
      }}
      location='bottom'
      visible={isBannerOpen ? bannerVisibility : 'hidden'}
      buttonText='Accept All'
      declineButtonText='Only Use Essential Cookies'
      enableDeclineButton
      flipButtons
      style={{
        background: '#F5F5F5',
        fontSize: '16px',
        fontFamily: '"gMedium", sans-serif',
        color: 'rgb(0, 0, 0, 0.85)',
      }}
      buttonClasses='cookie-accept-btn cookie-btn h-full pd-top-15 pd-bottom-15'
      buttonWrapperClasses={'cookie-btn-wrapper'}
      declineButtonClasses='cookie-decline-btn cookie-btn'
      containerClasses={
        'CookieConsent ' +
        (isFisheryProfilePage ? 'desktopCenter' : '') +
        (isSearchPage ? 'desktopRight' : '')
      }
      overlayStyle={{
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 11001,
      }}
      onAccept={handleAccept}
      onDecline={handleDecline}
      onOverlayClick={handleDismiss}
      expires={cookieExpiry}
      overlay={showOverlay}
    >
      <div className={'cookie_consent__badge'}>
        <img src={shieldImage} alt={'shield icon'} />
      </div>
      <div className={'cookie_consent__close'}>
        <Button
          icon={<CloseOutlined className={'fs-12'} />}
          shape='circle'
          type='default'
          size='small'
          onClick={handleDismiss}
        />
      </div>
      <div className={'cookie_consent__content'}>
        <h2 className={'cookie_consent__title black gSemiBold'}>
          We Respect Your Privacy.
        </h2>
        <span className={'cookie_consent__text black gRegular'}>
          We{' '}
          <a href={PRIVACY_POLICY_URL} className={'txt-underline'}>
            use cookies
          </a>{' '}
          to operate this website and provide the best possible user experience.
        </span>
      </div>
    </CookieConsent>
  );
}

function App() {
  const [showingCookieBanner, setShowingCookieBanner] = useState(
    !Boolean(getCookieConsentValue())
  );
  const [isCookieBannerForced, setIsCookieBannerForced] = useState(false);
  const [hasConsent, setHasConsent] = useState(hasCookieConsent);

  const cookiePreferences = useMemo(
    () => ({
      hasConsent,
      openCookiePreferences: () => {
        setIsCookieBannerForced(true);
        setShowingCookieBanner(true);
      },
    }),
    [hasConsent]
  );

  const handleCookieBannerClose = () => {
    setShowingCookieBanner(false);
    setIsCookieBannerForced(false);
  };

  return (
    <>
      <FlagsmithProvider
        options={{
          environmentID: process.env?.REACT_APP_FLAGSMITH_KEY,
        }}
        flagsmith={flagsmith}
      >
        <UserIdentificationProvider>
          <Router>
            <CookieConsentBanner
              forceOpen={isCookieBannerForced}
              onAccept={() => setHasConsent(true)}
              onClose={handleCookieBannerClose}
            />
            <div
              className={
                'App ' + (showingCookieBanner ? 'with-cookie-banner' : '')
              }
            >
              <CookiePreferencesContext.Provider value={cookiePreferences}>
                <BaseRouter />
              </CookiePreferencesContext.Provider>
            </div>
          </Router>
        </UserIdentificationProvider>
      </FlagsmithProvider>
    </>
  );
}

export default App;
